import React from 'react';
import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';

export function Platform() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-white py-20 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[400px] h-[400px] bg-brand-100/30 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Platform
          </p>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 tracking-tight mb-4">
            Institutional-Grade<br />US Equity Access
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl">
            Built for modern brokerage integration
          </p>
        </div>
      </section>

      {/* Trading Features */}
      <section className="py-20 bg-gray-50 relative">
        <div className="absolute inset-0 dot-grid opacity-20" />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Features
          </p>
          <h2 className="text-4xl font-bold mb-12 text-gray-900 tracking-tight">Trading Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Market Access</h3>
              <ul className="space-y-3 text-gray-600 text-sm">
                <li className="flex items-start">
                  <span className="text-brand-600 mr-3 font-mono">--</span>
                  US equities (NYSE, NASDAQ, AMEX)
                </li>
                <li className="flex items-start">
                  <span className="text-brand-600 mr-3 font-mono">--</span>
                  Extended hours + 24/5 trading
                </li>
                <li className="flex items-start">
                  <span className="text-brand-600 mr-3 font-mono">--</span>
                  Fractional shares (minimum $1)
                </li>
                <li className="flex items-start">
                  <span className="text-brand-600 mr-3 font-mono">--</span>
                  OTC securities access
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Order Types</h3>
              <ul className="space-y-3 text-gray-600 text-sm">
                <li className="flex items-start">
                  <span className="text-brand-600 mr-3 font-mono">--</span>
                  Market orders
                </li>
                <li className="flex items-start">
                  <span className="text-brand-600 mr-3 font-mono">--</span>
                  Limit orders
                </li>
                <li className="flex items-start">
                  <span className="text-brand-600 mr-3 font-mono">--</span>
                  Stop and stop-limit orders
                </li>
                <li className="flex items-start">
                  <span className="text-brand-600 mr-3 font-mono">--</span>
                  Time-in-force: GTC, Day, IOC, FOK
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* API Examples */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Developer Experience
          </p>
          <h2 className="text-4xl font-bold mb-12 text-gray-900 tracking-tight">API Example</h2>
          <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Submit an Order</h3>
            <div className="bg-gray-900 border border-gray-800 p-6 rounded-lg overflow-x-auto">
              <pre className="font-mono text-sm text-gray-300">
                <code>
                  <span className="text-brand-400">POST</span>{' '}
                  <span className="text-white">/v1/orders</span>
                  {'\n'}
                  <span className="text-gray-500">Content-Type:</span> application/json
                  {'\n'}
                  <span className="text-gray-500">Authorization:</span> Bearer YOUR_API_KEY
                  {'\n\n'}
                  {'{\n'}
                  {'  '}<span className="text-gray-500">"symbol"</span>: <span className="text-brand-300">"AAPL"</span>,{'\n'}
                  {'  '}<span className="text-gray-500">"side"</span>: <span className="text-brand-300">"buy"</span>,{'\n'}
                  {'  '}<span className="text-gray-500">"quantity"</span>: <span className="text-brand-400">10</span>,{'\n'}
                  {'  '}<span className="text-gray-500">"type"</span>: <span className="text-brand-300">"limit"</span>,{'\n'}
                  {'  '}<span className="text-gray-500">"price"</span>: <span className="text-brand-400">150.00</span>{'\n'}
                  {'}'}
                </code>
              </pre>
            </div>
            <div className="mt-6">
              <Link
                to="/docs"
                className="text-brand-600 hover:text-brand-500 font-semibold inline-flex items-center text-sm transition-colors"
              >
                View Full API Documentation
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Options */}
      <section className="py-20 bg-gray-50 relative">
        <div className="absolute inset-0 dot-grid opacity-20" />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Connectivity
          </p>
          <h2 className="text-4xl font-bold mb-12 text-gray-900 tracking-tight">Integration Options</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:border-brand-400/50 transition-all duration-300 hover:shadow-md">
              <h3 className="text-lg font-semibold mb-2 text-gray-900">REST API</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                RESTful HTTP API for order management and account queries
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:border-brand-400/50 transition-all duration-300 hover:shadow-md">
              <h3 className="text-lg font-semibold mb-2 text-gray-900">WebSocket <span className="text-xs font-normal text-gray-400">(coming soon)</span></h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Real-time streaming for order updates and market data
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:border-brand-400/50 transition-all duration-300 hover:shadow-md">
              <h3 className="text-lg font-semibold mb-2 text-gray-900">FIX Protocol <span className="text-xs font-normal text-gray-400">(coming soon)</span></h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                FIX 4.2/4.4 support for enterprise integrations
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-6 rounded-xl hover:border-brand-400/50 transition-all duration-300 hover:shadow-md">
              <h3 className="text-lg font-semibold mb-2 text-gray-900">Sandbox</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Full-featured sandbox environment for testing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
            Performance
          </p>
          <h2 className="text-4xl font-bold mb-12 text-gray-900 tracking-tight">Infrastructure</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="font-mono text-5xl font-bold text-brand-600 mb-2">&lt;1ms</div>
              <p className="text-gray-500 text-sm">Sub-millisecond order processing</p>
            </div>
            <div className="text-center">
              <div className="font-mono text-5xl font-bold text-brand-600 mb-2">99.9%</div>
              <p className="text-gray-500 text-sm">Uptime target SLA</p>
            </div>
            <div className="text-center">
              <div className="font-mono text-5xl font-bold text-brand-600 mb-2">Rust</div>
              <p className="text-gray-500 text-sm">Built with Rust for performance</p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm">
            <h3 className="text-lg font-semibold mb-6 text-gray-900">Technology Stack</h3>
            <div className="flex flex-wrap gap-3">
              <span className="bg-gray-100 border border-gray-200 px-4 py-2 rounded-lg font-mono text-sm text-gray-700">Rust OMS</span>
              <span className="bg-gray-100 border border-gray-200 px-4 py-2 rounded-lg font-mono text-sm text-gray-700">PostgreSQL</span>
              <span className="bg-gray-100 border border-gray-200 px-4 py-2 rounded-lg font-mono text-sm text-gray-700">Apache Kafka</span>
              <span className="bg-gray-100 border border-gray-200 px-4 py-2 rounded-lg font-mono text-sm text-gray-700">AWS</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-gradient-to-r from-brand-600 to-brand-500 py-20 overflow-hidden">
        <div className="absolute inset-0 noise-texture" />
        <div className="container mx-auto px-4 max-w-4xl text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4 text-white tracking-tight">Ready to Integrate?</h2>
          <p className="text-xl mb-8 text-white/80">
            Get started with our comprehensive API documentation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/docs"
              className="bg-white text-brand-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-200 inline-block hover:scale-[1.02]"
            >
              View API Docs
            </Link>
            <Link
              to="/contact"
              className="border border-white/30 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-all duration-200 inline-block"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
